import { z } from "zod";

/**
 * People (ALP-84 S5): one page, two views. "Everyone" is every person across channels (the old Contacts page);
 * "Commenters" is everyone who commented on one account, with moderation counts and bulk actions (the old People page).
 * Every filter lives in the URL so a view can be shared, bookmarked and restored with Back.
 */
export const PEOPLE_VIEWS = ["everyone", "commenters"] as const;
export type PeopleView = (typeof PEOPLE_VIEWS)[number];

export const PEOPLE_SORTS = ["recent", "comments", "negative", "toxic"] as const;
export const MARK_FILTERS = ["all", "any", "harmful", "troll", "vip", "none"] as const;
export const TRI_FILTERS = ["all", "only", "exclude"] as const;
export const HAS_FILTERS = ["all", "hidden", "deleted", "hidden_or_deleted"] as const;

export type PeopleSort = (typeof PEOPLE_SORTS)[number];
export type MarkFilter = (typeof MARK_FILTERS)[number];
export type TriFilter = (typeof TRI_FILTERS)[number];
export type HasFilter = (typeof HAS_FILTERS)[number];

export const peopleSearchSchema = z.object({
  view: z.enum(PEOPLE_VIEWS).optional().catch(undefined),
  q: z.string().max(100).optional().catch(undefined),
  /** Everyone: one tag. */
  tag: z.string().max(60).optional().catch(undefined),
  /** Commenters: the account, sort and the filters behind "Filters". */
  account: z.string().optional().catch(undefined),
  sort: z.enum(PEOPLE_SORTS).optional().catch(undefined),
  mark: z.enum(MARK_FILTERS).optional().catch(undefined),
  blocked: z.enum(TRI_FILTERS).optional().catch(undefined),
  flagged: z.enum(TRI_FILTERS).optional().catch(undefined),
  has: z.enum(HAS_FILTERS).optional().catch(undefined),
  /** Commenters: cards (default today) or a compact table. */
  layout: z.enum(["cards", "table"]).optional().catch(undefined),
});
export type PeopleSearch = z.infer<typeof peopleSearchSchema>;

/** "Commenters" opens when the link asks for it, or when commenter-only filters are set; "Everyone" otherwise. */
export function viewOf(s: PeopleSearch): PeopleView {
  if (s.view) return s.view;
  return s.account || s.mark || s.blocked || s.flagged || s.has || s.sort ? "commenters" : "everyone";
}

/** How many of the filters behind "Filters" are on (the count shown on the button). */
export function activeFilterCount(s: PeopleSearch): number {
  return [s.mark, s.blocked, s.flagged, s.has].filter((v) => v && v !== "all").length;
}

/** Drop the commenter-only keys when switching to Everyone (and the tag when switching to Commenters). */
export function switchView(s: PeopleSearch, view: PeopleView): PeopleSearch {
  const q = s.q ? { q: s.q } : {};
  if (view === "everyone") return { view, ...q, ...(s.tag ? { tag: s.tag } : {}) };
  return { view, ...q, ...(s.account ? { account: s.account } : {}), ...(s.sort ? { sort: s.sort } : {}), ...(s.layout ? { layout: s.layout } : {}) };
}

/** Last account picked in Commenters (per browser), so the view reopens where you left it. */
const LAST_ACCOUNT = "malfak.people.account";
export function rememberAccount(id: string) {
  try {
    localStorage.setItem(LAST_ACCOUNT, id);
  } catch {
    /* private mode */
  }
}
export function lastAccount(): string | null {
  try {
    return localStorage.getItem(LAST_ACCOUNT);
  } catch {
    return null;
  }
}

/**
 * Which account Commenters shows: the one in the URL, else the last one used (if it still exists), else the connected
 * account with the biggest audience (the brand's main account), else the first one. No account is skipped by name.
 */
export function pickAccount(accounts: { id: string; status?: string; followers?: number | null }[], fromUrl: string | undefined, remembered: string | null): string | null {
  if (fromUrl && accounts.some((a) => a.id === fromUrl)) return fromUrl;
  if (remembered && accounts.some((a) => a.id === remembered)) return remembered;
  const active = accounts.filter((a) => a.status === "active").sort((a, b) => (b.followers ?? 0) - (a.followers ?? 0));
  return active[0]?.id ?? accounts[0]?.id ?? null;
}
